import { Temporal } from 'temporal-polyfill';

import { DateInputError, resolvePlainDateTime } from './date-engine.js';

const MONTHS_LONG = Object.freeze([
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]);

const MONTHS_SHORT = Object.freeze(MONTHS_LONG.map((month) => month.slice(0, 3)));

const WEEKDAYS_LONG = Object.freeze([
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]);

const WEEKDAYS_SHORT = Object.freeze(WEEKDAYS_LONG.map((day) => day.slice(0, 3)));

const TOKEN_TABLES = Object.freeze({
  moment: Object.freeze({
    YYYY: 'year4',
    MMMM: 'monthLong',
    MMM: 'monthShort',
    MM: 'month2',
    M: 'month',
    DD: 'day2',
    D: 'day',
    dddd: 'weekdayLong',
    ddd: 'weekdayShort',
    HH: 'hour24_2',
    H: 'hour24',
    hh: 'hour12_2',
    h: 'hour12',
    mm: 'minute2',
    m: 'minute',
    ss: 'second2',
    s: 'second',
    SSS: 'millisecond3',
    A: 'ampmUpper',
    a: 'ampmLower',
    Z: 'offsetColon',
  }),
  'date-fns': Object.freeze({
    yyyy: 'year4',
    MMMM: 'monthLong',
    MMM: 'monthShort',
    MM: 'month2',
    M: 'month',
    dd: 'day2',
    d: 'day',
    EEEE: 'weekdayLong',
    EEE: 'weekdayShort',
    HH: 'hour24_2',
    H: 'hour24',
    hh: 'hour12_2',
    h: 'hour12',
    mm: 'minute2',
    m: 'minute',
    ss: 'second2',
    s: 'second',
    SSS: 'millisecond3',
    a: 'ampmLocale',
    XXX: 'offsetColon',
  }),
  luxon: Object.freeze({
    yyyy: 'year4',
    MMMM: 'monthLong',
    MMM: 'monthShort',
    MM: 'month2',
    M: 'month',
    dd: 'day2',
    d: 'day',
    cccc: 'weekdayLong',
    ccc: 'weekdayShort',
    HH: 'hour24_2',
    H: 'hour24',
    hh: 'hour12_2',
    h: 'hour12',
    mm: 'minute2',
    m: 'minute',
    ss: 'second2',
    s: 'second',
    SSS: 'millisecond3',
    a: 'ampmLocale',
    ZZZ: 'offsetCompact',
    ZZ: 'offsetColon',
  }),
  strftime: Object.freeze({
    '%Y': 'year4',
    '%B': 'monthLong',
    '%b': 'monthShort',
    '%m': 'month2',
    '%d': 'day2',
    '%e': 'daySpace',
    '%A': 'weekdayLong',
    '%a': 'weekdayShort',
    '%H': 'hour24_2',
    '%I': 'hour12_2',
    '%M': 'minute2',
    '%S': 'second2',
    '%L': 'millisecond3',
    '%p': 'ampmUpper',
    '%:z': 'offsetColon',
    '%z': 'offsetCompact',
  }),
  php: Object.freeze({
    Y: 'year4',
    F: 'monthLong',
    M: 'monthShort',
    m: 'month2',
    n: 'month',
    d: 'day2',
    j: 'day',
    l: 'weekdayLong',
    D: 'weekdayShort',
    H: 'hour24_2',
    G: 'hour24',
    h: 'hour12_2',
    g: 'hour12',
    i: 'minute2',
    s: 'second2',
    v: 'millisecond3',
    A: 'ampmUpper',
    a: 'ampmLower',
    P: 'offsetColon',
    O: 'offsetCompact',
  }),
});

export const DIALECTS = Object.freeze({
  moment: Object.freeze({
    id: 'moment',
    label: 'Day.js / Moment',
    examplePattern: 'YYYY-MM-DD HH:mm:ss.SSS Z',
    tokens: Object.freeze(['YYYY', 'MM', 'DD', 'HH', 'mm', 'ss', 'SSS', 'A', 'Z']),
    literalHelp: 'Wrap literal text in [square brackets].',
  }),
  'date-fns': Object.freeze({
    id: 'date-fns',
    label: 'date-fns / Unicode',
    examplePattern: "yyyy-MM-dd HH:mm:ss.SSS XXX",
    tokens: Object.freeze(['yyyy', 'MM', 'dd', 'HH', 'mm', 'ss', 'SSS', 'a', 'XXX']),
    literalHelp: "Wrap literal text in single quotes.",
  }),
  luxon: Object.freeze({
    id: 'luxon',
    label: 'Luxon',
    examplePattern: 'yyyy-MM-dd HH:mm:ss.SSS ZZZ',
    tokens: Object.freeze(['yyyy', 'MM', 'dd', 'HH', 'mm', 'ss', 'SSS', 'a', 'ZZZ']),
    literalHelp: "Wrap literal text in single quotes.",
  }),
  strftime: Object.freeze({
    id: 'strftime',
    label: 'strftime',
    examplePattern: '%Y-%m-%d %H:%M:%S.%L %:z',
    tokens: Object.freeze(['%Y', '%m', '%d', '%H', '%M', '%S', '%L', '%p', '%:z']),
    literalHelp: 'Use %% for a literal percent sign.',
  }),
  php: Object.freeze({
    id: 'php',
    label: 'PHP date',
    examplePattern: 'Y-m-d H:i:s.v P',
    tokens: Object.freeze(['Y', 'm', 'd', 'H', 'i', 's', 'v', 'A', 'P']),
    literalHelp: 'Prefix a literal letter with a backslash.',
  }),
});

const ASCII_LETTER = /[A-Za-z]/;
const MAX_PATTERN_LENGTH = 256;
const MAX_INPUT_LENGTH = 1_024;

function customError(message, code = 'INVALID_CUSTOM_VALUE', details = {}) {
  return new DateInputError(message, code, details);
}

function requireDialect(dialect) {
  if (!Object.hasOwn(DIALECTS, dialect)) {
    throw customError(`Unknown custom date dialect: ${String(dialect)}`, 'INVALID_CUSTOM_DIALECT', {
      dialect,
      supportedDialects: Object.keys(DIALECTS),
    });
  }

  return dialect;
}

function pushLiteral(parts, value) {
  if (!value) return;
  const previous = parts.at(-1);
  if (previous?.kind === 'literal') previous.value += value;
  else parts.push({ kind: 'literal', value });
}

function findToken(pattern, index, table) {
  const tokens = Object.keys(table).sort((a, b) => b.length - a.length);
  return tokens.find((token) => pattern.startsWith(token, index));
}

function readQuotedLiteral(pattern, start, dialect) {
  let value = '';
  let index = start + 1;

  while (index < pattern.length) {
    if (pattern[index] !== "'") {
      value += pattern[index];
      index += 1;
      continue;
    }

    if (pattern[index + 1] === "'") {
      value += "'";
      index += 2;
      continue;
    }

    return { value, nextIndex: index + 1 };
  }

  throw customError(`Unclosed quoted literal in ${dialect} pattern.`, 'INVALID_CUSTOM_PATTERN', {
    dialect,
    pattern,
  });
}

function tokenizeCustomPattern(dialect, pattern) {
  requireDialect(dialect);

  if (typeof pattern !== 'string' || pattern.length === 0) {
    throw customError('A custom date pattern is required.', 'INVALID_CUSTOM_PATTERN', { dialect, pattern });
  }
  if (pattern.length > MAX_PATTERN_LENGTH) {
    throw customError(`Custom patterns are limited to ${MAX_PATTERN_LENGTH} characters.`, 'INVALID_CUSTOM_PATTERN', {
      dialect,
      pattern,
    });
  }

  const table = TOKEN_TABLES[dialect];
  const parts = [];
  let index = 0;

  while (index < pattern.length) {
    if (dialect === 'moment' && pattern[index] === '[') {
      const end = pattern.indexOf(']', index + 1);
      if (end === -1) {
        throw customError('Unclosed [literal] in Moment pattern.', 'INVALID_CUSTOM_PATTERN', {
          dialect,
          pattern,
        });
      }
      pushLiteral(parts, pattern.slice(index + 1, end));
      index = end + 1;
      continue;
    }

    if ((dialect === 'moment' || dialect === 'php') && pattern[index] === '\\') {
      if (index + 1 >= pattern.length) {
        throw customError('A trailing backslash cannot escape a literal.', 'INVALID_CUSTOM_PATTERN', {
          dialect,
          pattern,
        });
      }
      pushLiteral(parts, pattern[index + 1]);
      index += 2;
      continue;
    }

    if ((dialect === 'date-fns' || dialect === 'luxon') && pattern[index] === "'") {
      if (pattern[index + 1] === "'") {
        pushLiteral(parts, "'");
        index += 2;
        continue;
      }
      const literal = readQuotedLiteral(pattern, index, dialect);
      pushLiteral(parts, literal.value);
      index = literal.nextIndex;
      continue;
    }

    if (dialect === 'strftime') {
      if (pattern[index] !== '%') {
        pushLiteral(parts, pattern[index]);
        index += 1;
        continue;
      }
      if (pattern[index + 1] === '%') {
        pushLiteral(parts, '%');
        index += 2;
        continue;
      }
      const token = findToken(pattern, index, table);
      if (!token) {
        throw customError(`Unsupported strftime token near “${pattern.slice(index, index + 3)}”.`, 'INVALID_CUSTOM_PATTERN', {
          dialect,
          pattern,
          index,
        });
      }
      parts.push({ kind: 'token', token, type: table[token] });
      index += token.length;
      continue;
    }

    const token = findToken(pattern, index, table);
    if (token) {
      parts.push({ kind: 'token', token, type: table[token] });
      index += token.length;
      continue;
    }

    if (ASCII_LETTER.test(pattern[index])) {
      throw customError(`Unsupported ${DIALECTS[dialect].label} token near “${pattern.slice(index, index + 4)}”.`, 'INVALID_CUSTOM_PATTERN', {
        dialect,
        pattern,
        index,
      });
    }

    pushLiteral(parts, pattern[index]);
    index += 1;
  }

  if (!parts.some((part) => part.kind === 'token')) {
    throw customError('A custom pattern must contain at least one date or time token.', 'INVALID_CUSTOM_PATTERN', {
      dialect,
      pattern,
    });
  }

  return parts;
}

function pad(value, length = 2) {
  return String(value).padStart(length, '0');
}

function formatYear(year) {
  if (!Number.isInteger(year) || year < 0 || year > 9_999) {
    throw customError('Custom patterns support years 0000 through 9999.', 'CUSTOM_YEAR_OUT_OF_RANGE', { year });
  }
  return pad(year, 4);
}

function offsetCompact(offset) {
  return offset.replaceAll(':', '');
}

function formatToken(type, zoned) {
  const hour12 = zoned.hour % 12 || 12;
  const weekdayIndex = zoned.dayOfWeek % 7;

  switch (type) {
    case 'year4': return formatYear(zoned.year);
    case 'monthLong': return MONTHS_LONG[zoned.month - 1];
    case 'monthShort': return MONTHS_SHORT[zoned.month - 1];
    case 'month2': return pad(zoned.month);
    case 'month': return String(zoned.month);
    case 'day2': return pad(zoned.day);
    case 'day': return String(zoned.day);
    case 'daySpace': return String(zoned.day).padStart(2, ' ');
    case 'weekdayLong': return WEEKDAYS_LONG[weekdayIndex];
    case 'weekdayShort': return WEEKDAYS_SHORT[weekdayIndex];
    case 'hour24_2': return pad(zoned.hour);
    case 'hour24': return String(zoned.hour);
    case 'hour12_2': return pad(hour12);
    case 'hour12': return String(hour12);
    case 'minute2': return pad(zoned.minute);
    case 'minute': return String(zoned.minute);
    case 'second2': return pad(zoned.second);
    case 'second': return String(zoned.second);
    case 'millisecond3': return pad(zoned.millisecond, 3);
    case 'ampmUpper': return zoned.hour < 12 ? 'AM' : 'PM';
    case 'ampmLower': return zoned.hour < 12 ? 'am' : 'pm';
    case 'ampmLocale': return zoned.hour < 12 ? 'AM' : 'PM';
    case 'offsetColon': return zoned.offset;
    case 'offsetCompact': return offsetCompact(zoned.offset);
    default:
      throw customError(`Unsupported normalized custom token: ${type}`, 'INVALID_CUSTOM_PATTERN', { type });
  }
}

function toInstant(value, fieldName = 'instant') {
  try {
    return Temporal.Instant.from(value);
  } catch (cause) {
    throw customError(`Invalid ${fieldName}.`, 'INVALID_CUSTOM_VALUE', { fieldName, cause: cause.message });
  }
}

export function formatCustom(instant, zone, dialect, pattern) {
  const parts = tokenizeCustomPattern(dialect, pattern);
  validateParsePattern(parts, dialect, pattern);
  let zoned;
  try {
    zoned = toInstant(instant).toZonedDateTimeISO(zone);
  } catch (cause) {
    if (cause instanceof DateInputError) throw cause;
    throw customError(`Unknown or invalid time zone: ${String(zone)}`, 'INVALID_TIME_ZONE', {
      zone,
      cause: cause.message,
    });
  }

  return parts.map((part) => (
    part.kind === 'literal' ? part.value : formatToken(part.type, zoned)
  )).join('');
}

function escapeRegex(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function regexForToken(type) {
  switch (type) {
    case 'year4': return '[0-9]{4}';
    case 'monthLong': return MONTHS_LONG.join('|');
    case 'monthShort': return MONTHS_SHORT.join('|');
    case 'month2': return '[0-9]{2}';
    case 'month': return '[0-9]{1,2}';
    case 'day2': return '[0-9]{2}';
    case 'day': return '[0-9]{1,2}';
    case 'daySpace': return ' ?[0-9]{1,2}';
    case 'weekdayLong': return WEEKDAYS_LONG.join('|');
    case 'weekdayShort': return WEEKDAYS_SHORT.join('|');
    case 'hour24_2': return '[0-9]{2}';
    case 'hour24': return '[0-9]{1,2}';
    case 'hour12_2': return '[0-9]{2}';
    case 'hour12': return '[0-9]{1,2}';
    case 'minute2': return '[0-9]{2}';
    case 'minute': return '[0-9]{1,2}';
    case 'second2': return '[0-9]{2}';
    case 'second': return '[0-9]{1,2}';
    case 'millisecond3': return '[0-9]{3}';
    case 'ampmUpper':
    case 'ampmLower':
    case 'ampmLocale': return 'AM|PM';
    case 'offsetColon': return '[+-][0-9]{2}:[0-9]{2}(?::[0-9]{2})?';
    case 'offsetCompact': return '[+-][0-9]{4}(?:[0-9]{2})?';
    default:
      throw customError(`Unsupported normalized custom token: ${type}`, 'INVALID_CUSTOM_PATTERN', { type });
  }
}

function validateParsePattern(parts, dialect, pattern) {
  const types = new Set(parts.filter((part) => part.kind === 'token').map((part) => part.type));
  const hasHour12 = types.has('hour12') || types.has('hour12_2');
  const hasHour24 = types.has('hour24') || types.has('hour24_2');
  const hasAmPm = types.has('ampmUpper') || types.has('ampmLower') || types.has('ampmLocale');

  if (hasHour12 !== hasAmPm) {
    throw customError('12-hour custom patterns must include an AM/PM token, and AM/PM requires a 12-hour token.', 'INVALID_CUSTOM_PATTERN', {
      dialect,
      pattern,
    });
  }
  if (hasHour12 && hasHour24) {
    throw customError('A custom pattern cannot combine 12-hour and 24-hour tokens.', 'INVALID_CUSTOM_PATTERN', {
      dialect,
      pattern,
    });
  }
}

function compileParser(parts, dialect, pattern) {
  validateParsePattern(parts, dialect, pattern);
  const captures = [];
  let source = '^';

  for (const part of parts) {
    if (part.kind === 'literal') {
      source += escapeRegex(part.value);
    } else {
      source += `(${regexForToken(part.type)})`;
      captures.push(part);
    }
  }
  source += '$';

  return { regex: new RegExp(source, 'iu'), captures };
}

function numeric(value) {
  return Number.parseInt(value.trim(), 10);
}

function nameIndex(value, names) {
  const normalized = value.toLocaleLowerCase('en-US');
  return names.findIndex((name) => name.toLocaleLowerCase('en-US') === normalized);
}

function assignParsed(parsed, key, value, token) {
  if (Object.hasOwn(parsed, key) && parsed[key] !== value) {
    throw customError(`Conflicting values were parsed for ${key}.`, 'INVALID_CUSTOM_VALUE', {
      key,
      previous: parsed[key],
      value,
      token,
    });
  }
  parsed[key] = value;
}

function normalizeOffset(value) {
  const match = /^([+-])(\d{2}):?(\d{2})(?::?(\d{2}))?$/.exec(value);
  if (!match) {
    throw customError('The numeric UTC offset is malformed.', 'INVALID_CUSTOM_VALUE', { offset: value });
  }
  const hour = Number(match[2]);
  const minute = Number(match[3]);
  const second = Number(match[4] ?? 0);
  if (hour > 23 || minute > 59 || second > 59) {
    throw customError('The numeric UTC offset is outside its valid range.', 'INVALID_CUSTOM_VALUE', { offset: value });
  }
  return `${match[1]}${match[2]}:${match[3]}${match[4] ? `:${match[4]}` : ''}`;
}

function consumeToken(parsed, type, raw, token) {
  switch (type) {
    case 'year4': assignParsed(parsed, 'year', numeric(raw), token); break;
    case 'monthLong': assignParsed(parsed, 'month', nameIndex(raw, MONTHS_LONG) + 1, token); break;
    case 'monthShort': assignParsed(parsed, 'month', nameIndex(raw, MONTHS_SHORT) + 1, token); break;
    case 'month2':
    case 'month': assignParsed(parsed, 'month', numeric(raw), token); break;
    case 'day2':
    case 'day':
    case 'daySpace': assignParsed(parsed, 'day', numeric(raw), token); break;
    case 'weekdayLong': assignParsed(parsed, 'weekday', nameIndex(raw, WEEKDAYS_LONG), token); break;
    case 'weekdayShort': assignParsed(parsed, 'weekday', nameIndex(raw, WEEKDAYS_SHORT), token); break;
    case 'hour24_2':
    case 'hour24': assignParsed(parsed, 'hour24', numeric(raw), token); break;
    case 'hour12_2':
    case 'hour12': assignParsed(parsed, 'hour12', numeric(raw), token); break;
    case 'minute2':
    case 'minute': assignParsed(parsed, 'minute', numeric(raw), token); break;
    case 'second2':
    case 'second': assignParsed(parsed, 'second', numeric(raw), token); break;
    case 'millisecond3': assignParsed(parsed, 'millisecond', numeric(raw), token); break;
    case 'ampmUpper':
    case 'ampmLower':
    case 'ampmLocale': assignParsed(parsed, 'ampm', raw.toUpperCase(), token); break;
    case 'offsetColon':
    case 'offsetCompact': assignParsed(parsed, 'offset', normalizeOffset(raw), token); break;
    default:
      throw customError(`Unsupported normalized custom token: ${type}`, 'INVALID_CUSTOM_PATTERN', { type });
  }
}

function validateClock(parsed) {
  if (parsed.hour24 !== undefined && (parsed.hour24 < 0 || parsed.hour24 > 23)) {
    throw customError('Hour must be between 0 and 23.', 'INVALID_CUSTOM_VALUE', { hour: parsed.hour24 });
  }
  if (parsed.hour12 !== undefined && (parsed.hour12 < 1 || parsed.hour12 > 12)) {
    throw customError('12-hour clock values must be between 1 and 12.', 'INVALID_CUSTOM_VALUE', { hour: parsed.hour12 });
  }
  if (parsed.minute !== undefined && (parsed.minute < 0 || parsed.minute > 59)) {
    throw customError('Minute must be between 0 and 59.', 'INVALID_CUSTOM_VALUE', { minute: parsed.minute });
  }
  if (parsed.second !== undefined && (parsed.second < 0 || parsed.second > 59)) {
    throw customError('Second must be between 0 and 59; leap seconds are not supported.', 'INVALID_CUSTOM_VALUE', {
      second: parsed.second,
    });
  }
}

function hourFromParsed(parsed, referenceHour) {
  if (parsed.hour24 !== undefined) return parsed.hour24;
  if (parsed.hour12 === undefined) return referenceHour;
  const base = parsed.hour12 % 12;
  return parsed.ampm === 'PM' ? base + 12 : base;
}

function sameInstant(left, right) {
  return left.epochNanoseconds === right.epochNanoseconds;
}

function resolutionForOffset(resolution, zone, wantedOffset) {
  const choices = [
    { instant: resolution.instant, fold: resolution.fold },
  ];
  if (resolution.alternatives) {
    choices.push(
      { instant: resolution.alternatives.earlier, fold: 'earlier' },
      { instant: resolution.alternatives.later, fold: 'later' },
    );
  }

  const uniqueChoices = choices.filter((choice, index, all) => (
    all.findIndex((candidate) => sameInstant(candidate.instant, choice.instant)) === index
  ));
  const matching = uniqueChoices.find((choice) => (
    normalizeOffset(choice.instant.toZonedDateTimeISO(zone).offset) === wantedOffset
  ));

  if (!matching) {
    throw customError(`The supplied offset ${wantedOffset} is not valid in ${zone} at that local time.`, 'CUSTOM_OFFSET_MISMATCH', {
      zone,
      offset: wantedOffset,
    });
  }

  return {
    ...resolution,
    instant: matching.instant,
    fold: resolution.alternatives ? matching.fold : 'none',
  };
}

export function parseCustom(text, zone, dialect, pattern, referenceInstant, disambiguation = 'earlier') {
  if (typeof text !== 'string' || text.length === 0) {
    throw customError('A custom date value is required.', 'INVALID_CUSTOM_VALUE', { text });
  }
  if (text.length > MAX_INPUT_LENGTH) {
    throw customError(`Custom date values are limited to ${MAX_INPUT_LENGTH} characters.`, 'INVALID_CUSTOM_VALUE');
  }

  const parts = tokenizeCustomPattern(dialect, pattern);
  const { regex, captures } = compileParser(parts, dialect, pattern);
  const match = regex.exec(text);
  if (!match) {
    throw customError(`The value does not fully match the ${DIALECTS[dialect].label} pattern.`, 'CUSTOM_PATTERN_MISMATCH', {
      dialect,
      pattern,
      text,
    });
  }

  const parsed = {};
  captures.forEach((capture, index) => consumeToken(parsed, capture.type, match[index + 1], capture.token));
  validateClock(parsed);

  let reference;
  try {
    reference = toInstant(referenceInstant ?? Temporal.Now.instant(), 'reference instant').toZonedDateTimeISO(zone);
  } catch (cause) {
    if (cause instanceof DateInputError) throw cause;
    throw customError(`Unknown or invalid time zone: ${String(zone)}`, 'INVALID_TIME_ZONE', {
      zone,
      cause: cause.message,
    });
  }

  let plain;
  try {
    plain = Temporal.PlainDateTime.from({
      year: parsed.year ?? reference.year,
      month: parsed.month ?? reference.month,
      day: parsed.day ?? reference.day,
      hour: hourFromParsed(parsed, reference.hour),
      minute: parsed.minute ?? reference.minute,
      second: parsed.second ?? reference.second,
      millisecond: parsed.millisecond ?? reference.millisecond,
      microsecond: 0,
      nanosecond: 0,
    }, { overflow: 'reject' });
  } catch (cause) {
    throw customError('The custom date contains an impossible calendar or clock value.', 'INVALID_CUSTOM_VALUE', {
      dialect,
      pattern,
      text,
      cause: cause.message,
    });
  }

  if (parsed.weekday !== undefined && plain.dayOfWeek % 7 !== parsed.weekday) {
    throw customError('The weekday does not agree with the calendar date.', 'CUSTOM_WEEKDAY_MISMATCH', {
      suppliedWeekday: WEEKDAYS_LONG[parsed.weekday],
      actualWeekday: WEEKDAYS_LONG[plain.dayOfWeek % 7],
    });
  }

  let resolution = resolvePlainDateTime(plain, zone, disambiguation);
  if (parsed.offset !== undefined) {
    resolution = resolutionForOffset(resolution, zone, parsed.offset);
  }
  return resolution;
}

function isValidDate(year, month, day) {
  try {
    Temporal.PlainDate.from({ year, month, day }, { overflow: 'reject' });
    return true;
  } catch {
    return false;
  }
}

function timeSuffixPattern(match) {
  if (!match?.groups?.hour) return '';
  let suffix = ' HH:mm';
  if (match.groups.second !== undefined) suffix += ':ss';
  if (match.groups.millisecond !== undefined) suffix += '.SSS';
  return suffix;
}

function candidate({ pattern, order, label, components, ambiguous = false }) {
  return Object.freeze({
    dialect: 'moment',
    pattern,
    label,
    dateOrder: order,
    ambiguous,
    components: Object.freeze({ ...components }),
  });
}

function orderedDateCandidates(first, second, year, patterns, dateOrder, timeSuffix) {
  const choices = [];
  const definitions = {
    MDY: { month: first, day: second, pattern: patterns.MDY, label: 'Month / day / year' },
    DMY: { month: second, day: first, pattern: patterns.DMY, label: 'Day / month / year' },
  };
  const preferred = dateOrder === 'DMY' ? ['DMY', 'MDY'] : ['MDY', 'DMY'];

  for (const order of preferred) {
    const definition = definitions[order];
    if (!isValidDate(year, definition.month, definition.day)) continue;
    if (choices.some((item) => item.month === definition.month && item.day === definition.day)) continue;
    choices.push({ order, ...definition });
  }

  const ambiguous = choices.length > 1;
  return choices.map((choice) => candidate({
    pattern: `${choice.pattern}${timeSuffix}`,
    order: choice.order,
    label: choice.label,
    ambiguous,
    components: { year, month: choice.month, day: choice.day },
  }));
}

export function inferCustomCandidates(text, dateOrder = 'MDY', referenceInstant) {
  if (typeof text !== 'string') return [];
  const value = text.trim();
  if (!value || value.length > MAX_INPUT_LENGTH) return [];

  let referenceYear;
  try {
    referenceYear = toInstant(referenceInstant ?? Temporal.Now.instant(), 'reference instant')
      .toZonedDateTimeISO('UTC').year;
  } catch {
    return [];
  }

  const compact = /^(?<date>\d{8})(?:[ T](?<hour>\d{2}):(?<minute>\d{2})(?::(?<second>\d{2})(?:\.(?<millisecond>\d{3}))?)?)?$/.exec(value);
  if (compact) {
    const digits = compact.groups.date;
    const suffix = timeSuffixPattern(compact);
    const firstYear = Number(digits.slice(0, 4));
    const ymdMonth = Number(digits.slice(4, 6));
    const ymdDay = Number(digits.slice(6, 8));
    if (firstYear >= 1_000 && isValidDate(firstYear, ymdMonth, ymdDay)) {
      return [candidate({
        pattern: `YYYYMMDD${suffix}`,
        order: 'YMD',
        label: 'Year / month / day',
        components: { year: firstYear, month: ymdMonth, day: ymdDay },
      })];
    }

    const year = Number(digits.slice(4));
    if (year < 1_000) return [];
    return orderedDateCandidates(
      Number(digits.slice(0, 2)),
      Number(digits.slice(2, 4)),
      year,
      { MDY: 'MMDDYYYY', DMY: 'DDMMYYYY' },
      dateOrder,
      suffix,
    );
  }

  const delimited = /^(?<first>\d{1,4})(?<separator>[./-])(?<second>\d{1,2})\k<separator>(?<third>\d{1,4})(?:[ T](?<hour>\d{2}):(?<minute>\d{2})(?::(?<secondClock>\d{2})(?:\.(?<millisecondClock>\d{3}))?)?)?$/.exec(value);
  if (delimited) {
    const { first, second, third, separator } = delimited.groups;
    const suffixMatch = {
      groups: {
        hour: delimited.groups.hour,
        second: delimited.groups.secondClock,
        millisecond: delimited.groups.millisecondClock,
      },
    };
    const suffix = timeSuffixPattern(suffixMatch);
    if (first.length === 4) {
      const year = Number(first);
      const month = Number(second);
      const day = Number(third);
      if (!isValidDate(year, month, day)) return [];
      const monthToken = second.length === 2 ? 'MM' : 'M';
      const dayToken = third.length === 2 ? 'DD' : 'D';
      return [candidate({
        pattern: `YYYY${separator}${monthToken}${separator}${dayToken}${suffix}`,
        order: 'YMD',
        label: 'Year / month / day',
        components: { year, month, day },
      })];
    }
    if (third.length !== 4) return [];

    const firstToken = first.length === 2 ? 'MM' : 'M';
    const secondToken = second.length === 2 ? 'DD' : 'D';
    const dmyFirst = first.length === 2 ? 'DD' : 'D';
    const dmySecond = second.length === 2 ? 'MM' : 'M';
    return orderedDateCandidates(
      Number(first),
      Number(second),
      Number(third),
      {
        MDY: `${firstToken}${separator}${secondToken}${separator}YYYY`,
        DMY: `${dmyFirst}${separator}${dmySecond}${separator}YYYY`,
      },
      dateOrder,
      suffix,
    );
  }

  const monthDay = /^(?<first>\d{1,2})(?<separator>[./-])(?<second>\d{1,2})$/.exec(value);
  if (monthDay) {
    const { first, second, separator } = monthDay.groups;
    const firstToken = first.length === 2 ? 'MM' : 'M';
    const secondToken = second.length === 2 ? 'DD' : 'D';
    const dmyFirst = first.length === 2 ? 'DD' : 'D';
    const dmySecond = second.length === 2 ? 'MM' : 'M';
    return orderedDateCandidates(
      Number(first),
      Number(second),
      referenceYear,
      {
        MDY: `${firstToken}${separator}${secondToken}`,
        DMY: `${dmyFirst}${separator}${dmySecond}`,
      },
      dateOrder,
      '',
    ).map((item) => Object.freeze({ ...item, assumedYear: referenceYear }));
  }

  return [];
}
